import { useEffect, useMemo, useRef } from 'react';
import { shallowEqual, useLocation, useSelector } from 'umi';
import {
  useCopilotOpenBridge,
} from '@/pages/aigc/pages/Agent/Copilot/hooks/useCopilotOpenBridge';
import { useLocalState } from '@/pages/aigc/pages/Agent/ChatPage/hooks/useLocalState';
import { markAsShownToday } from '@home/utils/bubbleHelpers';
import {
  hasAnyForcePopupTriggeredToday,
  markPageTriggered,
  markUserClosed,
  resolveForceCopilotPage,
  shouldTriggerForcePopup,
} from '@home/utils/forceCopilotHelpers';

const SHOW_COPILOT_KEY = '__dba_show_copilot__';

/** Copilot 浮窗根节点选择器（见 src/pages/aigc/pages/Agent/Copilot/index.scss） */
const COPILOT_CONTAINER_SELECTOR = '.dbh-agent-copilot';

/** 强弹打开后，若用户 10s 内无任何交互 → 自动关闭 Copilot */
const FORCE_POPUP_IDLE_AUTO_CLOSE_DELAY = 10_000;

interface IHomeSelectorState {
  home?: {
    userInfo?: {
      openid?: string;
      username?: string;
      // 历史拼写
      usename?: string;
      permissions?: Record<string, unknown>;
    };
  };
}

/**
 * Copilot 强制弹窗触发逻辑 Hook（仅供 AgentRobot 使用）
 *
 * 行为：
 * - 当用户进入 /dashboard、/intelligence、/opinion 三大页之一，
 *   且当日该用户未主动关闭过 Copilot 强引导、当前 page 当日未触发过时，
 *   直接打开 Copilot 面板（复用已有 Copilot 弹窗组件，无需独立 UI）。
 * - 触发时同步调用 `markAsShownToday()`，使 useBubbleLogic 当日不再展示
 *   greeting 气泡。
 * - 强弹打开 Copilot 后，若用户 10s 内在 Copilot 浮窗内未发生任何交互，
 *   自动关闭 Copilot（视为"被动错过"，不写 userClosed，其他三大页仍可触发）。
 * - 监听 Copilot 由"强弹打开"后被用户主动关闭（`__dba_show_copilot__` true → false），
 *   写入 userClosed=true，当日全局不再弹。
 */
export const useForceCopilotPopup = (): void => {
  const location = useLocation() as unknown as { pathname: string };
  const userInfo = useSelector(
    (state: IHomeSelectorState) => state.home?.userInfo,
    shallowEqual,
  );
  const { openCopilot, hideCopilot, isHaveAIPermission } = useCopilotOpenBridge();
  const [isShowCopilot] = useLocalState<boolean>(SHOW_COPILOT_KEY, false);

  // 用户唯一标识（兜底顺序：username → usename → openid）
  const userId = useMemo(() => {
    return userInfo?.username || userInfo?.usename || userInfo?.openid || '';
  }, [userInfo?.username, userInfo?.usename, userInfo?.openid]);

  const currentPage = useMemo(
    () => resolveForceCopilotPage(location.pathname || ''),
    [location.pathname],
  );

  /** 是否由"强制弹窗"打开的 Copilot —— 用于区分用户主动开启 vs 强引导开启 */
  const forceOpenedRef = useRef(false);
  /** 缓存 userId / hideCopilot，避免在副作用闭包中读取旧值 */
  const userIdRef = useRef(userId);
  const hideCopilotRef = useRef(hideCopilot);
  /** 自动关闭定时器 */
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** 闲置监听是否已挂载 */
  const idleListenersBoundRef = useRef(false);
  /** 闲置自动关闭是否已被触发（防止 hide → 监听到 false → 错误写 userClosed） */
  const autoClosedByIdleRef = useRef(false);

  useEffect(() => {
    userIdRef.current = userId;
  }, [userId]);

  useEffect(() => {
    hideCopilotRef.current = hideCopilot;
  }, [hideCopilot]);

  // ── 工具函数：清理 idle 定时器与全局监听 ──
  const clearIdleAutoClose = (): void => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
    if (idleListenersBoundRef.current) {
      document.removeEventListener('mousedown', handleCopilotInteraction, true);
      document.removeEventListener('keydown', handleCopilotInteraction, true);
      document.removeEventListener('wheel', handleCopilotInteraction, true);
      document.removeEventListener('touchstart', handleCopilotInteraction, true);
      idleListenersBoundRef.current = false;
    }
  };

  // ── 全局事件监听器：判断事件是否发生在 Copilot 浮窗内 ──
  // 注意：因 clearIdleAutoClose 与该函数互相引用，须挂在 hook 顶层作用域；
  // 用 function 声明 + 通过 ref 调用，避免依赖闭包外的可变值
  function handleCopilotInteraction(e: Event): void {
    const target = e.target as Node | null;
    if (!target) return;
    // 只关心发生在 Copilot 浮窗内的交互
    const insideCopilot
      = target instanceof Element
        ? !!target.closest(COPILOT_CONTAINER_SELECTOR)
        : false;
    if (!insideCopilot) return;
    // 用户已有交互 → 取消自动关闭，并解除监听
    clearIdleAutoClose();
  }

  // ── 启动 idle 自动关闭 ──
  const startIdleAutoClose = (): void => {
    clearIdleAutoClose();
    autoClosedByIdleRef.current = false;
    idleListenersBoundRef.current = true;
    document.addEventListener('mousedown', handleCopilotInteraction, true);
    document.addEventListener('keydown', handleCopilotInteraction, true);
    document.addEventListener('wheel', handleCopilotInteraction, true);
    document.addEventListener('touchstart', handleCopilotInteraction, true);

    idleTimerRef.current = setTimeout(() => {
      idleTimerRef.current = null;
      // 标记：这次关闭是 idle 自动关闭，不应写 userClosed
      autoClosedByIdleRef.current = true;
      // 强弹标志也清掉（自动关闭不算用户主动拒绝）
      forceOpenedRef.current = false;
      clearIdleAutoClose();
      hideCopilotRef.current();
    }, FORCE_POPUP_IDLE_AUTO_CLOSE_DELAY);
  };

  // === 触发副作用：进入三大页 + 满足触发条件 → 直接打开 Copilot ===
  useEffect(() => {
    if (!userId || !isHaveAIPermission || !currentPage) {
      return;
    }
    if (!shouldTriggerForcePopup(userId, currentPage)) {
      return;
    }
    // 标记该 page 已触发 + 屏蔽当日 greeting 气泡
    markPageTriggered(userId, currentPage);
    markAsShownToday();
    forceOpenedRef.current = true;
    openCopilot({ form: `force_popup_${currentPage}` });
    // 启动 10s 闲置自动关闭（用户在 Copilot 内任意交互即取消）
    startIdleAutoClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, isHaveAIPermission, currentPage, openCopilot]);

  // === 监听 Copilot 关闭：若由强弹打开后被用户主动关闭 → 当日全局不再弹 ===
  const prevShowRef = useRef<boolean>(isShowCopilot);
  useEffect(() => {
    const prev = prevShowRef.current;
    prevShowRef.current = isShowCopilot;

    // true → false：Copilot 被关闭
    if (prev && !isShowCopilot) {
      // 任何关闭都终止 idle 定时器与监听
      const wasAutoClosed = autoClosedByIdleRef.current;
      autoClosedByIdleRef.current = false;
      clearIdleAutoClose();

      if (forceOpenedRef.current && !wasAutoClosed) {
        // 用户主动点 X / handleClickRobot → 写 userClosed=true
        forceOpenedRef.current = false;
        const uid = userIdRef.current;
        if (uid) markUserClosed(uid);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShowCopilot]);

  // 卸载兜底清理
  useEffect(() => {
    return () => {
      clearIdleAutoClose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

/**
 * 工具：是否应屏蔽 greeting 气泡
 * 供 useBubbleLogic 调用，避免当日已强引导后又冒 greeting 气泡。
 */
export const shouldSuppressGreetingBubble = (userId: string): boolean => {
  if (!userId) return false;
  return hasAnyForcePopupTriggeredToday(userId);
};
